/**
 * API Configuration for different environments
 * Determines which API Gateway URL to use based on environment
 */

export interface ApiConfig {
  baseURL: string;
  environment: 'dev' | 'test' | 'uat' | 'prod';
  gcpProjectId: string;
}

export const API_CONFIG: Record<string, ApiConfig> = {
  dev: {
    baseURL: 'https://dev-flowrithm-gateway-26opragl.uc.gateway.dev',
    environment: 'dev',
    gcpProjectId: 'flowrithm-dev',
  },
  test: {
    baseURL: 'https://test-flowrithm-gateway-4tw9xvzo.uc.gateway.dev',
    environment: 'test',
    gcpProjectId: 'flowrithm-test-483120',
  },
  // UAT and Prod to be configured later
  uat: {
    baseURL: '', // TBD
    environment: 'uat',
    gcpProjectId: '', // TBD
  },
  prod: {
    baseURL: '', // TBD
    environment: 'prod',
    gcpProjectId: '', // TBD
  },
};

/**
 * Get API configuration based on environment variable
 * Defaults to 'dev' if not specified
 */
interface WindowWithEnv extends Window {
  __ENV__?: {
    VITE_ENVIRONMENT?: string;
    VITE_API_BASE_URL?: string;
  };
}

export const getApiConfig = (): ApiConfig => {
  // Priority 1: Check if VITE_API_BASE_URL is explicitly set (highest priority)
  // This allows overriding the URL via environment variable, useful for:
  // - CI/CD pipelines that read from Terraform outputs
  // - Different deployment environments
  // - Local development with custom URLs
  let explicitBaseURL: string | undefined;
  
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) {
      explicitBaseURL = import.meta.env.VITE_API_BASE_URL;
    }
  } catch {
    // import.meta might not be available in all contexts
  }
  
  if (!explicitBaseURL && typeof window !== 'undefined' && (window as WindowWithEnv).__ENV__?.VITE_API_BASE_URL) {
    explicitBaseURL = (window as WindowWithEnv).__ENV__!.VITE_API_BASE_URL!;
  }
  
  if (!explicitBaseURL && typeof process !== 'undefined' && process.env?.VITE_API_BASE_URL) {
    explicitBaseURL = process.env.VITE_API_BASE_URL;
  }
  
  // If explicit URL is provided, use it with environment detection for other config
  if (explicitBaseURL) {
    // Still determine environment for gcpProjectId and other config
    let env: string = 'dev';
    
    try {
      if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_ENVIRONMENT) {
        env = import.meta.env.VITE_ENVIRONMENT;
      }
    } catch {
      // import.meta might not be available
    }
    
    if (env === 'dev' && typeof window !== 'undefined' && (window as WindowWithEnv).__ENV__?.VITE_ENVIRONMENT) {
      env = (window as WindowWithEnv).__ENV__!.VITE_ENVIRONMENT!;
    }
    
    if (env === 'dev' && typeof process !== 'undefined' && process.env?.VITE_ENVIRONMENT) {
      env = process.env.VITE_ENVIRONMENT;
    }
    
    const baseConfig = API_CONFIG[env as keyof typeof API_CONFIG] || API_CONFIG.dev;
    
    const config: ApiConfig = {
      baseURL: explicitBaseURL,
      environment: baseConfig.environment,
      gcpProjectId: baseConfig.gcpProjectId,
    };
    
    // Configuration loaded successfully
    
    return config;
  }
  
  // Priority 2: Use environment-based lookup from hardcoded map
  // For web: Vite uses import.meta.env
  // For Node.js/build: process.env
  // Check import.meta.env first (Vite), then window.__ENV__, then process.env
  let env: string = 'dev';
  
  // Check import.meta.env (Vite) - available in Vite environments
  // Vite provides import.meta.env with all VITE_* variables
  // Types are defined in vite-env.d.ts
  try {
    // Access import.meta.env directly - properly typed via vite-env.d.ts
    if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_ENVIRONMENT) {
      env = import.meta.env.VITE_ENVIRONMENT;
    }
  } catch {
    // import.meta might not be available in all contexts (e.g., Node.js)
  }
  
  // Check window.__ENV__ (custom injection) if import.meta didn't work
  if (env === 'dev' && typeof window !== 'undefined' && (window as WindowWithEnv).__ENV__?.VITE_ENVIRONMENT) {
    env = (window as WindowWithEnv).__ENV__!.VITE_ENVIRONMENT!;
  }
  
  // Check process.env (Node.js/build time) as fallback
  if (env === 'dev' && typeof process !== 'undefined' && process.env?.VITE_ENVIRONMENT) {
    env = process.env.VITE_ENVIRONMENT;
  }

  const config = API_CONFIG[env as keyof typeof API_CONFIG] || API_CONFIG.dev;
  
  // Validate that baseURL is set
  if (!config.baseURL) {
    console.warn(`API baseURL is not configured for environment: ${env}. Using dev environment.`);
    return API_CONFIG.dev;
  }
  
  return config;
};

